import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import { Share2 } from 'lucide-react';
import { Product } from '../../pages/leasing-store/types';
import { LeasingRequestController } from './LeasingRequestController';
import { useTranslation } from 'react-i18next';

interface ProductCardProps {
  id: string;
  name: string;
  description: string;
  image: string;
  price: number;
  monthlyPayment: number;
  category: string;
  condition: string;
  className?: string;
  paymentPeriods?: string;
  installmentPayment?: { amount: number; period: string };
}

export function ProductCard({
  id,
  name,
  description,
  image,
  price,
  monthlyPayment,
  category,
  condition,
  className,
  installmentPayment,
}: ProductCardProps) {
	const { t } = useTranslation();
  const [modalType, setModalType] = useState<'leasing' | 'location' | 'achat' | 'cash' | null>(null);

  const product: Product = {
    id,
    name,
    description,
    image,
    price,
    monthlyPayment,
    category,
    subcategory: '',
    condition,
    gallery: [],
    installment: {
      downPaymentPercentage: 0,
      downPayment: 0,
      installmentAmount: installmentPayment?.amount || 0
    },
    leasingPartner: '',
    supplier: '',
    availableFinancing: ['leasing', 'Paiement Échelonné', 'Cash'],
    availableDurations: ['12', '24', '36'],
  };

  const handleShare = () => {
    const shareData = {
      title: name,
		  text: t('store.product.shareText', {
			name,
			description,
			price: price.toLocaleString('en-US'),
			monthly: monthlyPayment.toLocaleString('en-US')
		  }),
      url: `${window.location.origin}/leasing-store/product/${id}`,
    };

    if (navigator.share) {
      navigator.share(shareData).catch((error) => console.error('Error sharing:', error));
    } else {
		  alert(t('store.product.shareNotSupported'));
    }
  };
  
  const handleCloseModal = () => {
    setModalType(null);
  };
  
  const handleSubmitRequest = (formData: any) => {
    console.log('Demande soumise:', formData);
    setModalType(null);
  };

  return (
    <div className={cn("group relative bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col h-full", className)}>
      <Link to={`/leasing-store/product/${id}`} className="flex flex-col flex-1">
        <div className="aspect-square w-full overflow-hidden rounded-t-lg bg-gray-200 relative">
          <img
            src={image}
            alt={name}
            className="h-full w-full object-cover object-center group-hover:opacity-75"
          />
          <button
            onClick={(e) => {
              e.preventDefault();
              handleShare();
            }}
            className="absolute top-3 right-3 p-1.5 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 hover:bg-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
			aria-label={t('store.product.share')}
          >
            <Share2 className="h-4 w-4 text-gray-700" />
          </button>
        </div>
        <div className="px-5 pt-5 pb-2 relative flex-1 flex flex-col">
          <h3 className="text-base font-medium text-gray-800 line-clamp-2 min-h-[3rem]">{name}</h3>
		  <p className="mt-2 text-sm text-gray-500">
			{t(`store.categories.${category}.name`, { defaultValue: category })}
		  </p>
		  <p className="mt-1 text-xs text-gray-400">
			{t(`store.filterOptions.conditions.${condition}`, { defaultValue: condition })}
		  </p>
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-900">
			  {t('store.product.priceWithAmount', { amount: price.toLocaleString('en-US') })}
            </p>
            <p className="mt-1 text-sm text-primary-600">
			  {t('store.product.orMonthlyLeasing', { amount: monthlyPayment.toLocaleString('en-US') })}
            </p>
          </div>
        </div>
      </Link>
      
      <div className="px-5 py-3 border-t border-gray-100">
        <div className="flex justify-between items-center text-sm text-gray-700 py-1">
		  <span className="font-semibold">{t('store.product.price')}:</span>
          <span>${price.toLocaleString('en-US')}</span>
        </div>
        <div className="flex justify-between items-center text-sm text-primary-600 py-1">
		  <span className="font-semibold">{t('store.product.monthlyPayment')}:</span>
		  <span>{t('store.product.perMonthAmount', { amount: monthlyPayment.toLocaleString('en-US') })}</span>
        </div>
        {installmentPayment && (
          <div className="flex justify-between items-center text-sm text-gray-700 py-1">
			<span className="font-semibold">{t('store.product.installment')}:</span>
			<span>
				{t('store.product.installmentAmount', {
					amount: installmentPayment.amount.toLocaleString('en-US'),
					period: installmentPayment.period
				})}
			</span>
          </div>
        )}
      </div>
      
      <div className="px-5 pt-2 pb-5 mt-auto">
        <Button
          onClick={() => setModalType('leasing')}
          className="w-full py-2.5"
        >
		  {t('store.product.order')}
        </Button>
      </div>

      {/* Modal de demande */}
      {modalType && (
        <LeasingRequestController
          product={product}
          requestType={modalType}
          isOpen={true}
          onClose={handleCloseModal}
          onSubmit={handleSubmitRequest}
          returnToProductDetail={true}
        />
      )}
    </div>
  );
}
